// Milestone cell color and text color mapping for all approval/review/status/async cases
// This config is used by MilestoneMatrixFromAPI to render cell background, text color, icon, and additional text

export type MilestoneCellConfig = {
  cellColor: string;
  textColor: string;
  icon?: 'calendar' | 'envelope' | 'arrow-circle' | 'lined' | null; // null allows explicit icon hiding
  additionalText?: string;
};

// ApprovalCondition: 'Approval Required' | 'Async' | 'NO Approval Required' | 'Both'
// ReviewType: 'Meeting' | 'Async' | 'TBD' | 'Not Needed'
// Status: 'Target' | 'Scheduled' | 'Not Approved' | 'Not Approved - To be Approved Async' | 'Approved' | 'Approved - Needs Async Follow-Up' | 'Replanning' | 'Re-Approved' | 'Sent' | 'Sent - Feedback Pending' | 'Complete'

export const milestoneCellColorConfig: Record<string, MilestoneCellConfig> = {
  // Approval Required
  'Approval Required|Meeting|Target': {
    cellColor: '#FFFFFF',
    textColor: '#222'
  },
  'Approval Required|Meeting|Scheduled': {
    cellColor: '#FFFFFF',
    textColor: '#222',
    icon: 'calendar'
  },
  'Approval Required|Meeting|Not Approved': {
    cellColor: '#fbeaea',
    textColor: '#222'
  },
  'Approval Required|Meeting|Not Approved - To be Approved Async': {
    cellColor: '#fbeaea', // Red background
    textColor: '#000000', // Black text
    icon: 'envelope'
  },
  'Approval Required|Meeting|NOT_APPROVED_TO_BE_APPROVED_ASYNC': {
    cellColor: '#fbeaea', // Red background
    textColor: '#000000', // Black text
    icon: 'envelope'
  },
  'Approval Required|Meeting|Replanning': {
    cellColor: '#fbeaea', // Red background
    textColor: '#000000', // Black text
    icon: 'arrow-circle'
  },
  'Approval Required|Meeting|REPLANNING': {
    cellColor: '#fbeaea', // Red background
    textColor: '#000000', // Black text
    icon: 'arrow-circle'
  },
  'Approval Required|Meeting|Approved': {
    cellColor: '#F3FFEE',
    textColor: '#222',
    icon: null // Hide checkmark icon for approved status as requested
  },
  'Approval Required|Meeting|Approved - Needs Async Follow-Up': {
    cellColor: '#F3FFEE',
    textColor: '#d31510',
    icon: 'envelope',
    additionalText: 'Follow-up'
  },
  // Add mapping for APPROVED_FOLLOW_UP status key
  'Approval Required|Meeting|APPROVED_FOLLOW_UP': {
    cellColor: '#F3FFEE',
    textColor: '#d31510',
    icon: 'envelope',
    additionalText: 'Follow-up'
  },
  // Add mapping for RE_APPROVED status key (in case it comes without hyphen)
  'Approval Required|Meeting|RE_APPROVED': {
    cellColor: '#F3FFEE',
    textColor: '#222',
    icon: 'arrow-circle'
  },

  // Approval Required Async
  'Approval Required|Async|Target': {
    cellColor: '#FFFFFF',
    textColor: '#222',
    icon: 'envelope'
  },
  'Approval Required|Async|Sent': {
    cellColor: '#FFFCE0',
    textColor: '#222',
    icon: 'envelope',
    additionalText: 'Due on'
  },
  'Approval Required|Async|Sent - Feedback Pending': {
    cellColor: '#FFFCE0',
    textColor: '#222',
    icon: 'envelope',
    additionalText: 'Pending'
  },
  'Approval Required|Async|Not Approved': {
    cellColor: '#fbeaea', // Red background
    textColor: '#000000', // Black text
    icon: 'envelope'
  },
  'Approval Required|Async|Approved': {
    cellColor: '#F3FFEE',
    textColor: '#222',
    icon: 'envelope'
  },
  'Approval Required|Async|Replanning': {
    cellColor: '#fbeaea',
    textColor: '#222',
    icon: 'arrow-circle'
  },
  'Approval Required|Async|Re-Approved': {
    cellColor: '#F3FFEE',
    textColor: '#222',
    icon: 'arrow-circle'
  },

  // NO Approval Required
  'NO Approval Required|Meeting|Target': {
    cellColor: '#FFFFFF', // White background
    textColor: '#000000', // Black text
    icon: null // Explicitly no icon
  },
  'NO Approval Required|Meeting|Scheduled': {
    cellColor: '#FFFFFF', // White background
    textColor: '#000000', // Black text
    icon: 'calendar'
  },
  'NO Approval Required|Meeting|Complete': {
    cellColor: '#F3FFEE', // Green background (standardized with Approved)
    textColor: '#000000', // Black text
    icon: null // Explicitly no icon
  },
  'NO Approval Required|Meeting|Replanning': {
    cellColor: '#fbeaea', // Red background
    textColor: '#000000', // Black text
    icon: 'arrow-circle'
  },

  'NO Approval Required|Async|Target': {
    cellColor: '#FFFFFF', // White background
    textColor: '#000000', // Black text
    icon: 'envelope'
  },
  'NO Approval Required|Async|Sent': {
    cellColor: '#FFFCE0', // Yellow background
    textColor: '#000000', // Black text
    icon: 'envelope',
    additionalText: 'Due on'
  },
  'NO Approval Required|Async|Sent - Feedback Pending': {
    cellColor: '#FFFCE0', // Yellow background
    textColor: '#000000', // Black text
    icon: 'envelope',
    additionalText: 'Pending'
  },
  'NO Approval Required|Async|Complete': {
    cellColor: '#F3FFEE', // Green background
    textColor: '#000000', // Black text
    icon: null // Explicitly no icon
  },
  'NO Approval Required|Async|Replanning': {
    cellColor: '#fbeaea', // Red background
    textColor: '#000000', // Black text
    icon: 'arrow-circle'
  },

  // Both (special cases)
  'Both|TBD|n/a': { cellColor: '#FFFFFF', textColor: '#EA3829' }, // Figma: Alias/content/visual/semantic/negative
  'Both|Not Needed|n/a': {
    cellColor: '#F3FFEE',
    textColor: '#222',
    icon: 'lined'
  }
};
